import api from './api';
import { streamPost } from './sseClient';

/** Tutor v2 (Tranche D1, #489): conversations with streamed answers via /api/ai/tutor. */
export interface TutorMessage {
    id?: number;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    pending?: boolean;
}

export interface TutorConversation {
    id: number;
    title: string | null;
    courseId: number | null;
    courseTitle: string | null;
    lessonId: number | null;
    lessonTitle: string | null;
    messageCount: number;
    createdAt: string;
    updatedAt: string;
    messages?: TutorMessage[];
}

export type QuickHelpType = 'explain' | 'example' | 'quiz' | 'summary';

export interface SendMessageHandlers {
    onDelta: (delta: string) => void;
    onDone: (final: { content: string; messageId: number | null }) => void;
    signal?: AbortSignal;
}

class TutorService {
    async createConversation(courseId: number, lessonId?: number): Promise<TutorConversation> {
        const { data } = await api.post('/ai/tutor/conversations', { courseId, lessonId });
        return data;
    }

    async getConversations(courseId?: number): Promise<TutorConversation[]> {
        const { data } = await api.get('/ai/tutor/conversations', { params: courseId ? { courseId } : {} });
        return data;
    }

    async getConversation(conversationId: number): Promise<TutorConversation> {
        const { data } = await api.get(`/ai/tutor/conversations/${conversationId}`);
        return data;
    }

    async deleteConversation(conversationId: number): Promise<void> {
        await api.delete(`/ai/tutor/conversations/${conversationId}`);
    }

    /** Streams the answer; resolves when the server sent `done` (or throws on `error`). */
    async sendMessage(conversationId: number, content: string, handlers: SendMessageHandlers): Promise<void> {
        let finished = false;
        await streamPost(`/ai/tutor/conversations/${conversationId}/messages`, { content }, {
            signal: handlers.signal,
            onEvent: (event) => {
                if (event.error) throw new Error(event.error);
                if (event.text) handlers.onDelta(event.text);
                if (event.done) {
                    finished = true;
                    handlers.onDone({ content: event.content ?? '', messageId: event.messageId ?? null });
                }
            },
        });
        if (!finished) throw new Error('Die Antwort wurde unterbrochen.');
    }

    async getQuickHelp(lessonId: number, helpType: QuickHelpType): Promise<string> {
        const { data } = await api.post('/ai/tutor/quick-help', { lessonId, helpType });
        return data.response;
    }

    getQuickHelpOptions(): { type: QuickHelpType; label: string; icon: string }[] {
        return [
            { type: 'explain', label: 'Einfacher erklärt', icon: '💡' },
            { type: 'example', label: 'Beispiel zeigen', icon: '📝' },
            { type: 'quiz', label: 'Teste mich', icon: '❓' },
            { type: 'summary', label: 'Zusammenfassung', icon: '📋' },
        ];
    }
}

export const tutorService = new TutorService();
export default tutorService;
